import { useMutation } from '@tanstack/react-query';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { APITypes } from '../../primary-record-types';
import { onInitiatedAutomaticPortalRefresh, onResetProfileConnectionMetadata } from './cacheKeys';

const intl = getIntl('useOneUpConnectSearch', {
    errorConnecting: 'Error connecting to one up health',
    connectSuccess: 'Successfully connected to one up health',
    errorInitiatingAutomaticRefresh: 'Error initiating automatic refresh',
    initiateAutomaticRefreshSuccess: 'Successfully initiated automatic refresh',
});

export type UseOneUpHealthConnectArgs = {
    medicalProfileId: string;
    onConnectUrlReceived: (connectUrl: string) => void;
};

export type InitiateAutomaticRefreshArgs = {
    healthSystemId: number;
};

export type useOneUpConnectSearchReturnValue = {
    connect: (args: APITypes.ehrIntegrationsService.OneUpHealthConnectRequest) => void;
    isConnecting: boolean;
    initiateAutomaticRefresh: (args: InitiateAutomaticRefreshArgs) => void;
    isInitiatingAutomaticRefresh: boolean;
};

export default function useOneUpHealthConnect({
    medicalProfileId,
    onConnectUrlReceived,
}: UseOneUpHealthConnectArgs): useOneUpConnectSearchReturnValue {
    const notify = useNotify();
    const connectMutation = useMutation({
        mutationFn: async (args: APITypes.ehrIntegrationsService.OneUpHealthConnectRequest) => {
            const response = await appAPI.post<APITypes.ehrIntegrationsService.OneUpHealthConnectResponse>(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.connect(medicalProfileId),
                {
                    body: args,
                },
            );

            onConnectUrlReceived(response.connectUrl);
        },
        onSuccess: () => {
            onResetProfileConnectionMetadata.resetCache({ medicalProfileId })();
            notify('success', intl('connectSuccess'));
        },
        onError: () => {
            notify('error', intl('errorConnecting'));
        },
    });

    const initiateAutomaticRefresh = useMutation({
        mutationFn: async (args: InitiateAutomaticRefreshArgs) => {
            return await appAPI.post(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.automaticRefresh(medicalProfileId, args.healthSystemId),
            );
        },
        onSuccess: () => {
            notify('success', intl('initiateAutomaticRefreshSuccess'));
            onInitiatedAutomaticPortalRefresh.portalMetadataResetHandler.resetCache({ medicalProfileId })();
            onInitiatedAutomaticPortalRefresh.usageHandler.resetCache({ medicalProfileId })();
        },
        onError: () => {
            notify('error', intl('errorInitiatingAutomaticRefresh'));
        },
    });

    return {
        connect: connectMutation.mutate,
        isConnecting: connectMutation.isPending,
        initiateAutomaticRefresh: initiateAutomaticRefresh.mutate,
        isInitiatingAutomaticRefresh: initiateAutomaticRefresh.isPending,
    };
}
